import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import MagicLineIcon from 'remixicon-react/MagicLineIcon';

interface AiToggleProps {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
    disabled?: boolean;
    attention?: boolean;
}

const AiToggle: React.FC<AiToggleProps> = ({
    enabled,
    onChange,
    disabled = false,
    attention = false,
}) => {
    const toggle = () => !disabled && onChange(!enabled);
    const title = enabled ? 'Disable AI search' : 'Enable AI search';

    return (
        <SwitchButton
            onClick={toggle}
            title={title}
            role="switch"
            aria-checked={enabled}
            $enabled={enabled}
            $disabled={disabled}
            data-testid="ai-toggle"
            type="button"
        >
            <SwitchTrack $enabled={enabled} $attention={attention && !enabled}>
                <SwitchLabel $enabled={enabled}>AI</SwitchLabel>
                <SwitchThumb $enabled={enabled}>
                    <MagicLineIcon size={12} />
                </SwitchThumb>
            </SwitchTrack>
        </SwitchButton>
    );
};

const AI_GRADIENT = 'linear-gradient(135deg, #d2197e 0%, #e86558 33%, #f29f24 66%, #fccf80 100%)';
const AI_GLOW = '0 0.2rem 0.8rem rgba(248, 192, 67, 0.4)';

const shimmer = keyframes`
    0% {
        color: #d2197e;
        border-color: #d2197e;
        box-shadow: 0 0.3rem 1.2rem rgba(210, 25, 126, 0.5);
    }
    33% {
        color: #e86558;
        border-color: #e86558;
        box-shadow: 0 0.3rem 1.2rem rgba(232, 101, 88, 0.5);
    }
    66% {
        color: #f29f24;
        border-color: #f29f24;
        box-shadow: 0 0.3rem 1.2rem rgba(242, 159, 36, 0.5);
    }
    100% {
        color: #d2197e;
        border-color: #d2197e;
        box-shadow: 0 0.3rem 1.2rem rgba(210, 25, 126, 0.5);
    }
`;

// Sweeps once at the start of each cycle, then rests for the remainder.
const sheen = keyframes`
    0% {
        background-position: 100% 0;
    }
    15%,
    100% {
        background-position: 0% 0;
    }
`;

const SHEEN_TIMING = '13s ease-in-out 3s 7';

// Same cycle as the sheen so the glow peaks while the band passes.
const sheenGlow = keyframes`
    0%,
    22%,
    100% {
        opacity: 0;
    }
    6%,
    12% {
        opacity: 0.6;
    }
`;

const SwitchTrack = styled.span<{ $enabled: boolean; $attention?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: ${props => (props.$enabled ? 'flex-start' : 'flex-end')};
    box-sizing: border-box;
    width: 4.8rem;
    height: 2.4rem;
    padding: 0 0.7rem;
    border: 0.1rem solid ${props => (props.$enabled ? '#e86558' : 'currentColor')};
    border-radius: 1.2rem;
    color: ${props => (props.$enabled ? '#1a1a1a' : props.theme.colors.text.secondary)};
    background: ${props => (props.$enabled ? AI_GRADIENT : 'transparent')};
    box-shadow: ${props => (props.$enabled ? AI_GLOW : 'none')};
    transition: all 0.25s ease;

    ${props =>
        props.$attention &&
        css`
            /* On a pseudo-element, since hover replaces the track's own animation and would restart it. */
            &::before {
                content: '';
                position: absolute;
                inset: -0.1rem;
                border-radius: inherit;
                pointer-events: none;
                box-shadow: 0 0.3rem 1.2rem rgba(248, 192, 67, 0.6);
                opacity: 0;
            }

            &::after {
                content: '';
                position: absolute;
                inset: -0.1rem;
                border-radius: inherit;
                pointer-events: none;
                background: linear-gradient(
                        115deg,
                        transparent 35%,
                        #d2197e 45%,
                        #f29f24 55%,
                        transparent 65%
                    )
                    100% 0 / 300% 100% no-repeat;
                /* Only tints the grey border, label and thumb; the white field behind stays white. */
                mix-blend-mode: color;
            }

            @media (prefers-reduced-motion: no-preference) {
                &::before {
                    animation: ${sheenGlow} ${SHEEN_TIMING};
                }

                &::after {
                    animation: ${sheen} ${SHEEN_TIMING};
                }
            }
        `}
`;

const SwitchThumb = styled.span<{ $enabled: boolean }>`
    position: absolute;
    top: 0.2rem;
    left: ${props => (props.$enabled ? 'calc(100% - 2rem)' : '0.2rem')};
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 1.8rem;
    height: 1.8rem;
    border: 0.1rem solid ${props => (props.$enabled ? '#1a1a1a' : 'currentColor')};
    border-radius: 50%;
    background: ${props => (props.$enabled ? '#1a1a1a' : 'currentColor')};

    svg {
        color: ${props => (props.$enabled ? '#fccf80' : '#1a1a1a')};
    }
    transition: left 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), background 0.25s ease;
`;

const SwitchLabel = styled.span<{ $enabled: boolean }>`
    font-size: ${props => props.theme.text.size.xsmall};
    font-weight: ${props => (props.$enabled ? 600 : 500)};
`;

const SwitchButton = styled.button<{ $enabled: boolean; $disabled: boolean }>`
    display: flex;
    padding: 0.2rem;
    background: none;
    border: none;
    cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${props => (props.$disabled ? 0.5 : 1)};

    &:hover ${SwitchTrack} {
        box-shadow: 0 0.3rem 1.2rem rgba(248, 192, 67, 0.6);
        ${props =>
            !props.$enabled &&
            css`
                animation: ${shimmer} 2.75s linear infinite;
            `}
    }
`;

export default AiToggle;
